import type { JSX } from 'react';
import { useCallback, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client/react';
import { CalendarClock } from 'lucide-react';
import { useToast } from '@admin-platform/ui/components';

import { Button } from '../../Button/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../Card/Card';
import { Input } from '../../Inputs/Input';
import { Label } from '../../Inputs/Label';
import MY_FOLLOW_UP_TASK_DEFAULT_DAYS from '../../../hooks/queries/myFollowUpTaskDefaultDays.query.graphql';
import type { MyFollowUpTaskDefaultDaysQuery } from '../../../hooks/queries/myFollowUpTaskDefaultDays.query.graphql';
import {
  clampFollowUpDays,
  DEFAULT_FOLLOW_UP_TASK_DAYS,
  MAX_FOLLOW_UP_TASK_DAYS,
  MIN_FOLLOW_UP_TASK_DAYS,
} from '../../../utils/followUpTask';

import UPDATE_MY_FOLLOW_UP_TASK_DEFAULT_DAYS from '../mutations/updateMyFollowUpTaskDefaultDays.mutation.graphql';
import type {
  UpdateMyFollowUpTaskDefaultDaysMutation,
  UpdateMyFollowUpTaskDefaultDaysMutationVariables,
} from '../mutations/updateMyFollowUpTaskDefaultDays.mutation.graphql';

import styles from './FollowUpSettings.module.css';

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

/**
 * Per-operator default for how many business days out a follow-up task is due when
 * sending email. Prospect compose turns the task on with this delay; client
 * compose leaves it off unless the operator adds it. Either surface can change
 * or skip the task on that send.
 */
export default function FollowUpSettings(): JSX.Element {
  const toast = useToast();
  const { data, loading } = useQuery<MyFollowUpTaskDefaultDaysQuery>(
    MY_FOLLOW_UP_TASK_DEFAULT_DAYS,
    { fetchPolicy: 'cache-and-network' },
  );
  const stored = data?.me?.followUpTaskDefaultDays ?? DEFAULT_FOLLOW_UP_TASK_DAYS;
  const [draft, setDraft] = useState<string | null>(null);
  const [updateDays, { loading: saving }] = useMutation<
    UpdateMyFollowUpTaskDefaultDaysMutation,
    UpdateMyFollowUpTaskDefaultDaysMutationVariables
  >(UPDATE_MY_FOLLOW_UP_TASK_DEFAULT_DAYS);

  const display = draft ?? String(stored);
  const parsed = Number(display);
  const dirty = draft !== null && clampFollowUpDays(parsed) !== stored;
  const invalid =
    display === '' ||
    !Number.isInteger(parsed) ||
    parsed < MIN_FOLLOW_UP_TASK_DAYS ||
    parsed > MAX_FOLLOW_UP_TASK_DAYS;

  const handleSave = useCallback(async () => {
    const days = clampFollowUpDays(parsed);
    try {
      await updateDays({ variables: { days } });
      setDraft(null);
      toast('Follow-up default updated', 'success');
    } catch (err) {
      toast(errorMessage(err, 'Could not update the follow-up default'), 'error');
    }
  }, [parsed, toast, updateDays]);

  return (
    <Card noHover>
      <CardHeader>
        <div className={styles.Header}>
          <span className={styles.HeaderIcon} aria-hidden="true">
            <CalendarClock />
          </span>
          <div className={styles.HeaderText}>
            <CardTitle>Email follow-up</CardTitle>
            <CardDescription>
              Default delay for the task created when you send email to a company that is not a
              client yet.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className={styles.Body}>
        <div className={styles.Field}>
          <Label htmlFor="follow-up-default-days">Due in</Label>
          <div className={styles.Row}>
            <div className={styles.Days}>
              <Input
                id="follow-up-default-days"
                type="number"
                min={MIN_FOLLOW_UP_TASK_DAYS}
                max={MAX_FOLLOW_UP_TASK_DAYS}
                step={1}
                value={display}
                onChange={(event) => setDraft(event.target.value)}
                disabled={loading || saving}
                aria-invalid={invalid}
                aria-describedby="follow-up-default-days-hint"
              />
            </div>
            <span className={styles.Suffix}>business days</span>
            <Button
              variant="primary"
              onClick={() => void handleSave()}
              disabled={loading || saving || !dirty || invalid}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
          <span id="follow-up-default-days-hint" className={styles.Hint}>
            Email to a non-client company creates a follow-up task this many business days out
            (weekends skipped). Client email leaves it off until you add one. You can change or skip
            the task on each send. ({MIN_FOLLOW_UP_TASK_DAYS}–{MAX_FOLLOW_UP_TASK_DAYS})
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
